import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Check, X, AlertCircle, Calendar, Users, Clock, Building2, Pencil } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';

const HODActionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { requestId } = useParams();

  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editWorkers, setEditWorkers] = useState('');
  const [editReason, setEditReason] = useState('');
  const [editErrors, setEditErrors] = useState({});
  const [isHOD, setIsHOD] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null); // 'accept' | 'reject' | 'edit'

  // Request data fetched from API
  const [requestData, setRequestData] = useState(null);

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const formattedDate = requestData?.requestDate ? (() => {
    try {
      return new Date(requestData.requestDate).toLocaleDateString('en-IN', {
        weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'
      });
    } catch { return requestData.requestDate; }
  })() : null;

  const checkAuth = async () => {
    const token = localStorage.getItem('floor_manager_token');
    if (!token) {
      navigate('/lkart/supervisor-login', {
        state: { redirectUrl: location.pathname + location.search }
      });
      return;
    }
    try {
      // 1. Verify token
      const verifyResponse = await axios.get(`${BASE_URL}/lenskart/verify-token`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const verifyData = verifyResponse.data;
      if (!verifyData?.success) {
        localStorage.removeItem('floor_manager_token');
        navigate('/lkart/supervisor-login', {
          state: { redirectUrl: location.pathname + location.search }
        });
        return;
      }
      setIsHOD(verifyData.user?.isHOD === true);

      // 2. Fetch request data
      const dataResponse = await axios.get(
        `${BASE_URL}/floor-admin/data/${requestId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (dataResponse.data?.success) {
        setRequestData(dataResponse.data.data);
      } else {
        toast.error(dataResponse.data?.message || 'Failed to load request data.');
      }
    } catch (error) {
      console.error('Auth or data fetch error:', error);
      // If it's an auth error (401/403), clear token and redirect
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('floor_manager_token');
        navigate('/lkart/supervisor-login', {
          state: { redirectUrl: location.pathname + location.search }
        });
        return;
      }
      toast.error(error.response?.data?.message || error.message || 'Failed to load request data.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const openEditModal = () => {
    setEditWorkers(requestData?.TotalWorkers?.toString() || '');
    setEditErrors({});
    setEditReason('');
    setShowEditModal(true);
  };

  const handleEdit = async () => {
    const errors = {};
    if (!editWorkers || parseInt(editWorkers) <= 0) errors.workers = 'Must be a positive number';
    if (!editReason.trim()) errors.reason = 'Reason is required';
    if (editReason.trim().length < 3) errors.reason = 'Minimum 3 characters';
    if (Object.keys(errors).length) { setEditErrors(errors); return; }

    setIsSubmitting(true);
    setSelectedAction('edit');
    try {
      const token = localStorage.getItem('floor_manager_token');
      const response = await axios.post(
        `${BASE_URL}/floor-admin/hod-action`,
        {
          requestId,
          action: 'edit',
          editedFields: {
            TotalWorkers: parseInt(editWorkers),
            slot: requestData?.slot,
            requestDate: new Date(requestData?.requestDate).toISOString(),
            ...(editReason.trim() && { reason: editReason.trim() }),
          },
        },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (response.data?.success) {
        setShowEditModal(false);
        setActionSuccess('edit');
      } else {
        toast.error(response.data?.message || 'Failed to update. Please try again.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to update. Please try again.');
    } finally {
      setIsSubmitting(false);
      setSelectedAction(null);
    }
  };

  const handleAccept = async () => {
    setIsSubmitting(true);
    setSelectedAction('accept');
    try {
      const token = localStorage.getItem('floor_manager_token');
      const response = await axios.post(
        `${BASE_URL}/floor-admin/hod-action`,
        { requestId, action: 'approve', ...(reason.trim() && { reason: reason.trim() }) },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (response.data?.success) {
        setActionSuccess('accept');
      } else {
        toast.error(response.data?.message || 'Failed to accept. Please try again.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to accept. Please try again.');
    } finally {
      setIsSubmitting(false);
      setSelectedAction(null);
    }
  };

  const handleReject = async () => {
    if (!reason.trim()) { toast.error('Please provide a reason for rejection'); return; }
    if (reason.trim().length < 3) { toast.error('Reason must be at least 3 characters'); return; }
    if (reason.trim().length > 200) { toast.error('Reason must not exceed 200 characters'); return; }

    setIsSubmitting(true);
    setSelectedAction('reject');
    try {
      const token = localStorage.getItem('floor_manager_token');
      const response = await axios.post(
        `${BASE_URL}/floor-admin/hod-action`,
        { requestId, action: 'reject', reason: reason.trim() },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (response.data?.success) {
        setActionSuccess('reject');
      } else {
        toast.error(response.data?.message || 'Failed to reject. Please try again.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to reject. Please try again.');
    } finally {
      setIsSubmitting(false);
      setSelectedAction(null);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (isLoading) return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Verifying authentication...</p>
      </div>
    </div>
  );

  // ── No Permission ──────────────────────────────────────────────────────────
  if (isHOD === false) return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="max-w-sm w-full bg-white rounded-lg shadow-xl p-8 text-center">
        <div className="flex justify-center mb-4">
          <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
            <X className="h-8 w-8 text-red-600" />
          </div>
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Access Denied</h2>
        <p className="text-gray-500 text-sm mb-6">
          This page is restricted to HODs only. You do not have permission to access this page.
        </p>
        <button
          onClick={() => navigate('/lkart/supervisor/requirements')}
          className="w-full py-2 px-4 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );

  // ── Success Screen ─────────────────────────────────────────────────────────
  if (actionSuccess) return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="max-w-sm w-full bg-white rounded-lg shadow-xl p-8 text-center">
        <div className="flex justify-center mb-4">
          <div className={`h-16 w-16 rounded-full flex items-center justify-center ${
            actionSuccess === 'reject' ? 'bg-red-100' : 'bg-green-100'
          }`}>
            {actionSuccess === 'reject'
              ? <X className="h-8 w-8 text-red-600" />
              : <Check className="h-8 w-8 text-green-600" />}
          </div>
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">
          {actionSuccess === 'accept' && 'Request Approved'}
          {actionSuccess === 'reject' && 'Request Rejected'}
          {actionSuccess === 'edit' && 'Request Updated'}
        </h2>
        <p className="text-gray-500 text-sm mb-6">
          {actionSuccess === 'accept' && 'The request has been approved successfully.'}
          {actionSuccess === 'reject' && 'The request has been rejected successfully.'}
          {actionSuccess === 'edit' && 'The worker count has been updated successfully.'}
        </p>
        <button
          onClick={() => navigate('/lkart/supervisor/requirements')}
          className="w-full py-2 px-4 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );

  // ── Main Page ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-6">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-3">
            <AlertCircle className="h-12 w-12 text-indigo-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800">Take Action</h2>
          <p className="text-gray-500 text-sm mt-1">HOD Review</p>
        </div>

        {requestData && (
          <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 mb-6 space-y-2">
            {formattedDate && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Calendar className="h-4 w-4 text-indigo-500 shrink-0" />
                <span className="font-medium text-gray-500 w-24">Date</span>
                <span>{formattedDate}</span>
              </div>
            )}
            {requestData.TotalWorkers && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Users className="h-4 w-4 text-indigo-500 shrink-0" />
                <span className="font-medium text-gray-500 w-24">Workers</span>
                <span>{requestData.TotalWorkers}</span>
                <button
                  onClick={openEditModal}
                  disabled={isSubmitting}
                  className="ml-auto text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded p-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Edit worker count"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              </div>
            )}
            {requestData.slot && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Clock className="h-4 w-4 text-indigo-500 shrink-0" />
                <span className="font-medium text-gray-500 w-24">Shift</span>
                <span>{requestData.slot}</span>
              </div>
            )}
            {requestData.department && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Building2 className="h-4 w-4 text-indigo-500 shrink-0" />
                <span className="font-medium text-gray-500 w-24">Department</span>
                <span>{requestData.department}</span>
              </div>
            )}
          </div>
        )}

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reason <span className="text-gray-400 text-xs">(Optional for approval, required for rejection)</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => { if (e.target.value.length <= 200) setReason(e.target.value); }}
            placeholder="Please provide a reason for your decision..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            rows="4"
            disabled={isSubmitting}
          />
          <div className="flex justify-between mt-1">
            <span className="text-xs text-gray-500">{reason.length}/200 characters</span>
            {reason.length > 0 && reason.length < 3 && (
              <span className="text-xs text-red-500">Minimum 3 characters required for rejection</span>
            )}
          </div>
        </div>

        <div className="flex gap-4">
          <button onClick={handleAccept} disabled={isSubmitting}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {isSubmitting && selectedAction === 'accept'
              ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              : <><Check className="h-5 w-5" /><span>Approve</span></>}
          </button>
          <button onClick={handleReject} disabled={isSubmitting || reason.trim().length < 3}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {isSubmitting && selectedAction === 'reject'
              ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              : <><X className="h-5 w-5" /><span>Reject</span></>}
          </button>
        </div>

        {/* Edit Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-1">Edit Request</h3>
              <p className="text-sm text-gray-500 mb-4">Only worker count can be modified.</p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Workers <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  value={editWorkers}
                  onChange={(e) => { setEditWorkers(e.target.value); setEditErrors(prev => ({ ...prev, workers: '' })); }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${editErrors.workers ? 'border-red-500' : 'border-gray-300'}`}
                  disabled={isSubmitting}
                />
                {editErrors.workers && <p className="text-xs text-red-500 mt-1">{editErrors.workers}</p>}
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={editReason}
                  onChange={(e) => {
                    if (e.target.value.length <= 200) setEditReason(e.target.value);
                  }}
                  placeholder="Reason for editing worker count..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  rows="3"
                  disabled={isSubmitting}
                />
                <span className="text-xs text-gray-500">{editReason.length}/200 characters</span>
                {editErrors.reason && <p className="text-xs text-red-500 mt-1">{editErrors.reason}</p>}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50">
                  Cancel
                </button>
                <button
                  onClick={handleEdit}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  {isSubmitting && selectedAction === 'edit'
                    ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    : 'Confirm Edit'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HODActionPage;